import {
  Container,
  Grid,
  GridItem,
  Text,
  Spinner,
  Box,
  Flex,
  Button,
  Center,
  VStack,
  Image,
} from "@chakra-ui/react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { ChatInput } from "@/components/ui/chat-input";
import HtmlRender from "@/components/ui/html-render";
import { useSSE } from "@/hooks/useSSE";
import { usePendingMessage } from "@/context/PendingMessageContext";
import { ChatService, StorageService } from "@/client";
import MessageActions from "@/components/Chats/MessageActions";

interface ChatMessage {
  id: string;
  role: string;
  content: string;
  metadata?: string[];
  additional_kwargs?: unknown;
  options?: Record<string, unknown>;
}

interface StreamEvent {
  type: string;
  content?: string;
}

export const Route = createFileRoute("/_layout/chat/$convId")({
  component: ConvMessages,
});

function ConvMessages() {
  const { convId } = Route.useParams();
  const navigate = useNavigate();
  const { pendingMessage, clearPendingMessage } = usePendingMessage();
  const queryClient = useQueryClient();
  const [userMessage, setUserMessage] = useState<ChatMessage | null>(null);
  const [currentImageFileId, setCurrentImageFileId] = useState<string | null>(
    null
  );
  const convoScrollRef = useRef<HTMLDivElement | null>(null);

  const { isStreaming, isThinking, streamEvents, messageContent, sendMessage } =
    useSSE({
      onStreamDone: () => {
        setUserMessage(null);
        queryClient.invalidateQueries({ queryKey: ["chatMessages", convId] });
      },
    });

  const {
    data: dbMessages,
    error: messagesError,
    isLoading: messagesLoading,
  } = useQuery({
    queryKey: ["chatMessages", convId],
    queryFn: () =>
      ChatService.readMessages({ convId }).then(
        (res: unknown) => (res as { data: ChatMessage[] }).data
      ),
    retry: false, // 404 에러 시 재시도하지 않음
  });

  // 삭제된 대화에 접근한 경우 처리
  useEffect(() => {
    if (
      messagesError &&
      (messagesError as { status?: number })?.status === 404
    ) {
      // 삭제된 대화인 경우 대화 목록으로 리다이렉션
      navigate({ to: "/conversations" });
    }
  }, [messagesError, navigate]);

  useEffect(() => {
    if (pendingMessage) {
      console.log("🔍 pendingMessage 처리:", pendingMessage);
      const msg: ChatMessage = {
        id: `user-${Date.now()}`,
        role: "user",
        content: pendingMessage.msg,
        options: pendingMessage.options,
      };
      setUserMessage(msg);

      // 이미지 file_id 추출
      const fileId = pendingMessage.options?.file_id as string | undefined;
      if (fileId) {
        setCurrentImageFileId(fileId);
      }

      sendMessage(convId, pendingMessage.msg, pendingMessage.options, () => {
        // 사용자 메시지는 스트리밍 완료 후에도 유지
      });
      clearPendingMessage();
    }
  }, [pendingMessage, clearPendingMessage, convId, sendMessage]);

  // 메시지 데이터 처리 로직을 컴포넌트 최상단에서 실행
  const metaBlockContents = (streamEvents as StreamEvent[])
    .filter((ev) =>
      ["thought", "action", "action_input", "observation"].includes(ev.type)
    )
    .map((ev) => {
      switch (ev.type) {
        case "thought":
          return `🧠 생각:\n${ev.content?.trim() || ""}`;
        case "action":
          return `▶️ 실행 도구:\n${ev.content?.trim() || ""}`;
        case "action_input":
          return `🔧 입력값:\n${ev.content?.trim() || ""}`;
        case "observation":
          return `👀 관찰결과:\n${ev.content?.trim() || ""}`;
        default:
          return "";
      }
    });

  const streamedMeta =
    metaBlockContents.length > 0
      ? [
          {
            id: "meta-block",
            role: "assistant-meta",
            content: "",
            metadata: metaBlockContents,
          },
        ]
      : [];

  const streamedAnswer = (streamEvents as StreamEvent[]).some(
    (ev) =>
      ev.type === "answer" ||
      ev.type === "content" ||
      ev.type === "search_results" ||
      ev.type === "reference"
  )
    ? {
        id:
          dbMessages?.[dbMessages.length - 1]?.id || `assistant-${Date.now()}`,
        role: "assistant",
        content:
          messageContent ||
          (streamEvents as StreamEvent[])
            .filter(
              (ev) =>
                ev.type === "answer" ||
                ev.type === "content" ||
                ev.type === "search_results" ||
                ev.type === "reference"
            )
            .map((ev) => ev.content)
            .join(""),
        searchResults: (streamEvents as StreamEvent[])
          .filter((ev) => ev.type === "search_results")
          .flatMap(
            (ev) =>
              (
                ev as unknown as {
                  search_results: Array<{
                    file_name: string;
                    file_path: string;
                    relevance_score: number;
                    page_info?: string;
                    metadata?: {
                      content?: string;
                      [key: string]: unknown;
                    };
                    pages?: string | Array<string>;
                  }>;
                }
              ).search_results || []
          ),
        reference:
          (streamEvents as StreamEvent[])
            .filter((ev) => ev.type === "reference")
            .map(
              (ev) =>
                (
                  ev as unknown as {
                    reference: {
                      search_results: Array<{
                        file_name: string;
                        file_path: string;
                        relevance_score: number;
                        page_info: string;
                        content?: string;
                        metadata?: {
                          content?: string;
                          [key: string]: unknown;
                        };
                        pages?: string | Array<string>;
                      }>;
                      files_used: Array<{
                        filename: string;
                        size: number;
                        content_type: string;
                      }>;
                      generation_command: string;
                    };
                  }
                ).reference
            )[0] || null,
      }
    : null;

  // 디버깅 로그 추가
  console.log("🔍 스트림 이벤트:", streamEvents);
  console.log("🔍 스트리밍 답변:", streamedAnswer);
  console.log("🔍 사용자 메시지:", userMessage);
  console.log("🔍 스트리밍 중:", isStreaming);

  // 검색 결과 감지 및 포맷팅
  const formatSearchResults = (
    content: string,
    searchResults?: Array<{
      file_name: string;
      file_path: string;
      relevance_score: number;
      page_info?: string; // 추가
      metadata?: {
        content?: string;
        [key: string]: unknown;
      };
      pages?: string | Array<string>;
    }>,
    reference?: {
      search_results: Array<{
        file_name: string;
        file_path: string;
        relevance_score: number;
        page_info?: string;
        metadata?: {
          content?: string;
          [key: string]: unknown;
        };
        pages?: string | Array<string>;
      }>;
      files_used: Array<{
        filename: string;
        size: number;
        content_type: string;
      }>;
      generation_command: string;
    }
  ) => {
    if (
      content.includes("검색 결과:") &&
      content.includes("개의 문서를 찾았습니다")
    ) {
      // 검색 결과를 더 깔끔하게 포맷팅
      const lines = content.split("\n");
      const formattedLines = lines.map((line) => {
        // 번호가 있는 라인 (1. 2. 등)을 강조
        if (/^\d+\./.test(line)) {
          return `**${line}**`;
        }
        return line;
      });

      // 검색 결과 상세 정보가 있으면 링크 추가
      if (searchResults && searchResults.length > 0) {
        formattedLines.push("\n### 📄 검색된 문서:");
        searchResults.forEach((result, index) => {
          const pages = Array.isArray(result.pages)
            ? result.pages.join(", ")
            : result.pages || "N/A";

          // relevance_score를 숫자로 변환
          const relevanceScore =
            typeof result.relevance_score === "string"
              ? Number.parseFloat(result.relevance_score)
              : result.relevance_score;

          formattedLines.push(
            `${index + 1}. **${result.file_name}** (관련도: ${relevanceScore?.toFixed(2) || "N/A"}, 페이지: ${pages})`
          );
        });
      }

      // reference 정보가 있으면 추가
      if (reference) {
        formattedLines.push("\n### 📚 참고 자료:");

        if (reference.search_results && reference.search_results.length > 0) {
          formattedLines.push("**검색된 문서:**");
          reference.search_results.forEach((result, index) => {
            const pages = Array.isArray(result.pages)
              ? result.pages.join(", ")
              : result.pages || "N/A";

            const relevanceScore =
              typeof result.relevance_score === "string"
                ? Number.parseFloat(result.relevance_score)
                : result.relevance_score;

            formattedLines.push(
              `${index + 1}. **${result.file_name}** (관련도: ${relevanceScore?.toFixed(2) || "N/A"}, 페이지: ${result.page_info || pages})`
            );
          });
        }

        if (reference.files_used && reference.files_used.length > 0) {
          formattedLines.push("\n**사용된 파일:**");
          reference.files_used.forEach((file, index) => {
            formattedLines.push(
              `${index + 1}. **${file.filename}** (크기: ${file.size} bytes, 타입: ${file.content_type})`
            );
          });
        }
      }

      return formattedLines.join("\n");
    }
    return content;
  };

  // DB에 사용자 메시지가 이미 존재하면 중복 표시 방지
  const dbHasUserMessage =
    userMessage &&
    (dbMessages ?? []).some(
      (msg: unknown) =>
        (msg as ChatMessage).role === "user" &&
        (msg as ChatMessage).content === userMessage.content
    );

  const mergedMessages: ChatMessage[] = [
    ...(dbMessages ?? []).flatMap((msg: unknown, index: number) => {
      const messages: ChatMessage[] = [];

      // ✅ assistant-meta 처리: metadata를 포맷 적용
      const msgData = msg as ChatMessage & {
        options?: Record<string, unknown>;
      };
      if (
        msgData.role === "assistant-meta" &&
        Array.isArray(msgData.metadata) &&
        msgData.metadata &&
        msgData.metadata.length > 0
      ) {
        const formattedMeta = msgData.metadata.map((line: string) => {
          if (line.startsWith("Thought:")) {
            return `🧠 생각:\n${line.replace("Thought:", "").trim()}`;
          }
          if (line.startsWith("Action:")) {
            return `▶️ 실행 도구:\n${line.replace("Action:", "").trim()}`;
          }
          if (line.startsWith("Action Input:")) {
            return `🔧 입력값:\n${line.replace("Action Input:", "").trim()}`;
          }
          if (line.startsWith("Observation:")) {
            return `👀 관찰결과:\n${line.replace("Observation:", "").trim()}`;
          }
          return line;
        });

        messages.push({
          id: `${index}-meta`,
          role: "assistant-meta",
          content: "",
          metadata: formattedMeta,
        });
      }

      messages.push({
        id: msgData.id || `${index}`,
        role: msgData.role,
        content: formatSearchResults(msgData.content),
        options: msgData.options,
      });

      return messages;
    }),
    ...(isStreaming && userMessage && !dbHasUserMessage ? [userMessage] : []),
    ...(isStreaming ? streamedMeta : []),
    ...(isStreaming && streamedAnswer
      ? [
          {
            ...streamedAnswer,
            content: formatSearchResults(
              streamedAnswer.content,
              streamedAnswer.searchResults,
              streamedAnswer.reference
            ),
          },
        ]
      : []),
  ];

  // DB 메시지에서 가장 최근 이미지 file_id 찾기
  useEffect(() => {
    if (dbMessages && dbMessages.length > 0) {
      // 사용자 메시지를 역순으로 찾아서 가장 최근의 이미지 file_id 가져오기
      for (let i = dbMessages.length - 1; i >= 0; i--) {
        const msg = dbMessages[i] as ChatMessage & {
          options?: Record<string, unknown>;
        };
        if (msg.role === "user" && msg.options?.file_id) {
          const fileId = msg.options.file_id as string;
          // 이미지 파일인지 확인 (content_type이 있으면 확인, 없으면 일단 설정)
          setCurrentImageFileId(fileId);
          break; // 가장 최근의 이미지 file_id만 사용
        }
      }
    }
  }, [dbMessages]);

  // 스크롤 효과를 항상 실행
  useEffect(() => {
    convoScrollRef.current?.scrollTo({
      top: convoScrollRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, []);

  // 로딩 중이거나 에러가 있는 경우 처리
  if (messagesLoading) {
    return (
      <Container maxW="container.xl" py={8}>
        <Center>
          <Spinner size="lg" />
        </Center>
      </Container>
    );
  }

  if (messagesError) {
    return (
      <Container maxW="container.xl" py={8}>
        <Center>
          <VStack gap={4}>
            <Text fontSize="lg" color="red.500">
              대화를 찾을 수 없습니다.
            </Text>
            <Button
              colorScheme="blue"
              onClick={() => navigate({ to: "/conversations" })}
            >
              대화 목록으로 돌아가기
            </Button>
          </VStack>
        </Center>
      </Container>
    );
  }

  return (
    <Grid
      templateColumns="68.5% 31.5%"
      templateRows="1fr auto"
      h="100%"
      w="1000px"
      mx="auto"
    >
      <GridItem
        ref={convoScrollRef}
        colStart={1}
        rowStart={1}
        overflowY="auto"
        px="16px"
        py="8px"
      >
        <Box>
          {mergedMessages
            .filter((msg) => msg.role !== "tool") // tool 역할 메시지 필터링
            .map((msg) => {
              console.log(
                "Message role:",
                msg.role,
                "id:",
                msg.id,
                "content:",
                msg.content.substring(0, 50)
              );
              return (
                <Box key={msg.id} mb="8px">
                  {msg.role === "assistant-meta" ? (
                    <CollapsableMetaBox metadata={msg.metadata ?? []} />
                  ) : (
                    <Container
                      bg={msg.role === "user" ? "blue.500" : "white"}
                      color={msg.role === "user" ? "white" : "gray.800"}
                      borderRadius="16px"
                      p="12px"
                      boxShadow="md"
                      border={msg.role === "user" ? "none" : "1px solid"}
                      borderColor={
                        msg.role === "user" ? "transparent" : "gray.200"
                      }
                    >
                      <HtmlRender
                        content={msg.content}
                        isUserMessage={msg.role === "user"}
                      />
                      {msg.role === "assistant" && !isStreaming && (
                        <MessageActions
                          messageId={msg.id}
                          content={msg.content}
                          onCopy={(content) => {
                            console.log("Copied content:", content);
                          }}
                        />
                      )}
                    </Container>
                  )}
                </Box>
              );
            })}

          {isThinking && (
            <Flex align="center" gap={2} mb="8px" ml="4px">
              <Spinner size="sm" color="gray.500" />
              <Text fontSize="sm" color="gray.500">
                생각 중...
              </Text>
            </Flex>
          )}

          <Box h="60px" />
        </Box>
      </GridItem>

      <GridItem colStart={1} rowStart={2} borderTop="1px solid gray.200" p="4">
        <Flex direction="column" gap={2}>
          <ChatInput
            isStreaming={isStreaming}
            onSend={(msg, options) => {
              const userMsg: ChatMessage = {
                id: `user-${Date.now()}`,
                role: "user",
                content: msg,
                options: options,
              };
              setUserMessage(userMsg);

              // 이미지 file_id 추출
              const fileId = options?.file_id as string | undefined;
              if (fileId) {
                setCurrentImageFileId(fileId);
              }

              sendMessage(convId, msg, options, () => {
                // 메시지는 ChatInput 컴포넌트 내부에서 자동으로 초기화됨
              });
            }}
          />
        </Flex>
      </GridItem>

      {/* 오른쪽 여백: 이미지 표시 */}
      <GridItem
        colStart={2}
        rowStart={1}
        rowSpan={2}
        borderLeft="1px solid gray.200"
        p="16px"
        overflowY="auto"
      >
        <ImageDisplay fileId={currentImageFileId} />
      </GridItem>
    </Grid>
  );
}

const CollapsableMetaBox = ({
  metadata,
  isStreaming = false,
}: {
  metadata: string[];
  isStreaming?: boolean;
}) => {
  const [isExpanded, setIsExpanded] = useState(true);

  useEffect(() => {
    if (!isStreaming) setIsExpanded(false);
  }, [isStreaming]);

  if (!metadata || metadata.length === 0) return null;

  return (
    <Box
      position="relative"
      bg="gray.100"
      borderRadius="md"
      border="1px solid"
      borderColor="gray.300"
      p="6px"
      mb="2px"
      fontSize="sm"
      color="gray.700"
      maxHeight={isExpanded ? "500px" : "100px"}
      overflow="auto"
      transition="max-height 0.3s ease"
      lineHeight="1.3"
    >
      <Box
        as="button"
        position="absolute"
        top="4px"
        right="8px"
        fontSize="12px"
        color="gray.500"
        cursor="pointer"
        onClick={() => setIsExpanded((prev) => !prev)}
        _hover={{ textDecoration: "underline" }}
      >
        {isExpanded ? "▲" : "▼"}
      </Box>
      {metadata.join("\n")}
    </Box>
  );
};

// 이미지 표시 컴포넌트
function ImageDisplay({ fileId }: { fileId: string | null }) {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!fileId) {
      setImageUrl(null);
      return;
    }

    let blobUrl: string | null = null;

    const loadImage = async () => {
      setIsLoading(true);
      try {
        // 먼저 presigned URL 시도
        const objectName = `temp/${fileId}`;
        let url: string | null = null;

        try {
          const response = await StorageService.getPresignedUrl({
            objectName,
          });
          url = (response as { url: string }).url;

          // presigned URL이 내부 네트워크 주소를 포함하는지 확인
          if (url && (url.includes("minio:") || url.includes("localhost:9000"))) {
            // 내부 주소인 경우 프록시 엔드포인트 사용
            throw new Error("Internal URL detected, using proxy");
          }

          // presigned URL로 이미지 로드 테스트
          const testResponse = await fetch(url, { method: "HEAD" });
          if (!testResponse.ok) {
            throw new Error("Presigned URL not accessible");
          }
        } catch (presignedError) {
          // presigned URL 실패 시 프록시 엔드포인트 사용
          console.warn("Presigned URL 접근 실패, 프록시 엔드포인트 사용:", presignedError);
          
          const apiBaseUrl = import.meta.env.VITE_API_URL || "";
          const proxyUrl = `${apiBaseUrl}/api/v1/storage/image/${fileId}`;
          
          // 프록시 엔드포인트로 이미지 다운로드하여 Blob URL 생성
          const response = await fetch(proxyUrl, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("access_token") || ""}`,
            },
          });

          if (!response.ok) {
            throw new Error(`프록시 엔드포인트 오류: ${response.status}`);
          }

          const blob = await response.blob();
          blobUrl = URL.createObjectURL(blob);
          url = blobUrl;
        }

        setImageUrl(url);
      } catch (error) {
        console.error("이미지 로드 실패:", error);
        setImageUrl(null);
      } finally {
        setIsLoading(false);
      }
    };

    loadImage();

    // Blob URL 정리
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [fileId]);

  if (!fileId) {
    return (
      <Box
        h="100%"
        display="flex"
        alignItems="center"
        justifyContent="center"
        color="gray.400"
      >
        <Text fontSize="sm">업로드된 이미지가 여기에 표시됩니다</Text>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box h="100%" display="flex" alignItems="center" justifyContent="center">
        <Spinner size="lg" />
      </Box>
    );
  }

  if (!imageUrl) {
    return (
      <Box
        h="100%"
        display="flex"
        alignItems="center"
        justifyContent="center"
        color="gray.400"
      >
        <Text fontSize="sm">이미지를 불러올 수 없습니다</Text>
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Box
        flex={1}
        borderRadius="md"
        overflow="hidden"
        border="1px solid"
        borderColor="gray.200"
        bg="gray.50"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Image
          src={imageUrl}
          alt="Uploaded image"
          maxW="100%"
          maxH="100%"
          objectFit="contain"
          onError={() => {
            setImageUrl(null);
          }}
        />
      </Box>
    </Box>
  );
}

export default ConvMessages;
